import { SSE_EVENTS } from "@lexicanon/shared"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  applyPipelineStep,
  deriveEffectiveProgress,
  type IMeetingCardProcessing,
  type IPipelineWeights,
  isProcessingStage,
  pipelineStarted,
  type TProcessingStage,
} from "../lib/pipeline-progress"

/** Per-meeting live progress inputs — the raw SSE signals before merging. The
 * persisted baseline is intentionally NOT stored here: the list page owns the
 * persisted seed and merges it when rendering, so this hook only tracks what the
 * event stream tells it. */
interface IMeetingProgressState {
  pipeline: IPipelineWeights | null
  sseStage: TProcessingStage | null
  sseProgress: number | null
}

interface IUseMeetingsPipelineProgress {
  /** Live per-card progress keyed by sanitized meeting id. Persisted fields are
   *  passed as null — merge any persisted seed at the call site. */
  progressById: ReadonlyMap<string, IMeetingCardProcessing>
  /** Feed a `PROCESSING_STAGE` SSE event. Signature matches `useMeetingEvents`'s
   *  `onStage` so the page can thread it straight through. */
  onStage: (meetingId: string, stage: string, progress?: number) => void
  /** Feed a `PIPELINE_STARTED` / `PIPELINE_STEP` SSE payload. */
  onPipeline: (meetingId: string, payload: Record<string, unknown>) => void
  /** Drop a meeting's tracked progress (e.g. once it lands on the summary). */
  clear: (meetingId: string) => void
}

/** Same sanitization the detail page applies before comparing/keying meeting ids. */
function safeId(meetingId: string): string {
  return meetingId.replace(/[^a-zA-Z0-9_-]/g, "_")
}

const EMPTY_STATE: IMeetingProgressState = { pipeline: null, sseStage: null, sseProgress: null }

/**
 * Tracks per-meeting finalize progress across many meetings at once, for the
 * meetings LIST cards. It does NOT open its own SSE connection — the list page
 * threads `onStage` / `onPipeline` into its existing `useMeetingEvents` call, so
 * one company event stream feeds every card. `progressById` is derived from the
 * shared pure math in `lib/pipeline-progress` (persisted seeds are the page's
 * concern, passed as null here).
 *
 * State resets when `activeOrgId` changes — a different tenant's meetings share
 * no progress, and stale ids must not leak across an org switch.
 */
export function useMeetingsPipelineProgress(activeOrgId: string): IUseMeetingsPipelineProgress {
  const [byId, setById] = useState<ReadonlyMap<string, IMeetingProgressState>>(() => new Map())

  // biome-ignore lint/correctness/useExhaustiveDependencies: activeOrgId is an intentional trigger — the body only resets, wiping another tenant's stale progress on an org switch.
  useEffect(() => {
    setById(new Map())
  }, [activeOrgId])

  const onStage = useCallback((meetingId: string, stage: string, progress?: number): void => {
    const key = safeId(meetingId)
    setById((prev) => {
      const current = prev.get(key) ?? EMPTY_STATE
      const next = new Map(prev)
      next.set(key, {
        pipeline: current.pipeline,
        sseStage: isProcessingStage(stage) ? stage : current.sseStage,
        sseProgress: typeof progress === "number" ? progress : current.sseProgress,
      })
      return next
    })
  }, [])

  const onPipeline = useCallback((meetingId: string, payload: Record<string, unknown>): void => {
    const key = safeId(meetingId)
    setById((prev) => {
      const current = prev.get(key) ?? EMPTY_STATE
      if (payload.type === SSE_EVENTS.PIPELINE_STARTED) {
        const next = new Map(prev)
        next.set(key, { ...current, pipeline: pipelineStarted(payload) })
        return next
      }
      if (payload.type === SSE_EVENTS.PIPELINE_STEP) {
        const next = new Map(prev)
        next.set(key, { ...current, pipeline: applyPipelineStep(current.pipeline, payload) })
        return next
      }
      return prev
    })
  }, [])

  const clear = useCallback((meetingId: string): void => {
    const key = safeId(meetingId)
    setById((prev) => {
      if (!prev.has(key)) return prev
      const next = new Map(prev)
      next.delete(key)
      return next
    })
  }, [])

  const progressById = useMemo<ReadonlyMap<string, IMeetingCardProcessing>>(() => {
    const out = new Map<string, IMeetingCardProcessing>()
    for (const [key, state] of byId) {
      out.set(
        key,
        deriveEffectiveProgress({
          pipeline: state.pipeline,
          sseStage: state.sseStage,
          sseProgress: state.sseProgress,
          persistedStage: null,
          persistedProgress: null,
        }),
      )
    }
    return out
  }, [byId])

  return { progressById, onStage, onPipeline, clear }
}
